import { PageController } from '@client/components/_core/layout/PageController';
import { Emails } from '@client/components/_shared/email/Emails';
import { useTrackEngagementView } from '@client/hooks/business/engagement/useTrackEngagementView';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { ENABLE_EMAIL_WORKLOAD_MANAGER_FLAG } from '@shared/constants/business/communication';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Alert } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
    selectedId: z.coerce.bigint().optional(),
});

export const Route = createFileRoute('/console/$orgId/workload-manager/emails')({
    staticData: {
        metadata: {
            title: 'Emails',
        },
        ui: {
            heightVariant: 'fit',
        },
    },
    validateSearch: zodValidator(searchSchema),
    component: EmailsRoute,
});

function EmailsRoute() {
    const enableEmailWorkloadManager = useFeatureFlag(ENABLE_EMAIL_WORKLOAD_MANAGER_FLAG);
    // Only count a view when the feature is actually shown (this route gates
    // in-render, unlike the complaints route which redirects in beforeLoad).
    useTrackEngagementView('workload_tab_viewed', { tab: 'emails' }, enableEmailWorkloadManager);
    const navigate = useNavigate();
    const orgId = useOrgId();

    const { selectedId: urlSelectedId } = useSearch({ strict: false });
    const [localSelectedId, setLocalSelectedId] = useState<bigint | undefined>();

    useEffect(() => {
        if (urlSelectedId !== localSelectedId) {
            setLocalSelectedId(urlSelectedId);
        }
    }, [urlSelectedId]);

    const handleSelectedIdChange = useCallback(
        (id: bigint | undefined) => {
            setLocalSelectedId(id);
            navigate({
                to: '/console/$orgId/workload-manager/emails',
                params: { orgId },
                search: { selectedId: id },
            });
        },
        [navigate, orgId],
    );

    if (!enableEmailWorkloadManager) {
        return <Alert title="This feature is currently under development." type="warning" />;
    }

    return (
        <PageController route={Route}>
            <Emails selectedId={localSelectedId} onSelectedIdChange={handleSelectedIdChange} />
        </PageController>
    );
}
